import { Grid, Menu, MenuItem } from "@mui/material";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { FieldArray, FieldArrayRenderProps, Form, Formik } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import { RewardCategoryFields } from "types/rewards";
import {
  backendErrors,
  parseTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { useStyles } from "./RewardCategoryForm.styles";

interface RewardCategoryModel extends RewardCategoryFields {
  rewardCategoryId: string;
}

export function RewardCategoryForm(props: {
  editMode?: boolean;
  initialValues?: RewardCategoryModel;
  onClose: () => void;
}) {
  const classes = useStyles();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const contextMenu = useContextMenu();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { addRewardCategory, editRewardCategory } = useReward();

  const initialValues =
    props.editMode && props.initialValues
      ? {
          active: props.initialValues.active,
          translations: [
            ...((props.initialValues.translations &&
              props.initialValues.translations
                ?.filter(
                  (translation: { locale: string }) =>
                    translation?.locale === defaultLang?.code,
                )
                .map((item: { locale: string; name: string }) => {
                  return {
                    code: item.locale,
                    name: item.name,
                    languageName: item.locale,
                  };
                })) ||
              []),
            ...((props.initialValues.translations &&
              props.initialValues.translations
                ?.filter(
                  (translation: { locale: string }) =>
                    translation?.locale !== defaultLang?.code,
                )
                .map((item: { locale: string; name: string }) => {
                  return {
                    code: item.locale,
                    name: item.name,
                    languageName: item.locale,
                  };
                })) ||
              []),
          ],
        }
      : {
          active: false,
          translations: [
            defaultLang && {
              code: defaultLang.code,
              languageName: defaultLang.name,
            },
          ],
        };

  const transformTranslations = (
    translations: { name?: string; code?: string }[],
  ) => {
    return translations.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.code as string]: { name: cur.name },
      }),
      {},
    );
  };

  return (
    <Formik
      initialStatus={{}}
      initialValues={initialValues}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});
        try {
          props.editMode && props.initialValues
            ? await editRewardCategory(props.initialValues.rewardCategoryId, {
                ...values,
                translations: transformTranslations(values.translations),
                sortOrder: 0,
              })
            : await addRewardCategory({
                ...values,
                translations: transformTranslations(values.translations),
                sortOrder: 0,
              });
          props.onClose();
          notify(
            message({
              id: props.editMode
                ? "rewardCategoryUpdated"
                : "rewardCategoryAdded",
            }),
          );
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          const parsedErrors = {
            ...errors,
            translations: parseTranslationsArrayError(
              errors.translations,
              values.translations,
            ),
          };
          setStatus(parsedErrors);
          focusError();
        }
      }}
    >
      {({ handleSubmit, isSubmitting, status, values, dirty }) => (
        <Form onSubmit={handleSubmit}>
          {status?.[""] && <span className={classes.error}>{status[""]}</span>}
          <FieldArray
            name="translations"
            render={(translationsArray: FieldArrayRenderProps) => (
              <>
                {values?.translations?.map(
                  (item: { languageName: string }, index: number) => {
                    const isFirstRow = index === 0;

                    return (
                      <Grid container key={index}>
                        <Grid item xs={isFirstRow ? 12 : 11} mb={8}>
                          <TextInput
                            label={
                              isFirstRow ? (
                                <RequiredFieldLabel
                                  text={`${message({
                                    id: "categoryName",
                                  })} (${item?.languageName})`}
                                />
                              ) : (
                                `${message({
                                  id: "categoryName",
                                })} (${item?.languageName})`
                              )
                            }
                            name={`translations[${index}].name`}
                            data-test-id={`reward-category-category-name-input-${index}`}
                            width="100%"
                          />
                        </Grid>
                        {!isFirstRow && (
                          <Grid item xs={1} mb={8}>
                            <div className={classes.clearIcon}>
                              <OLClearIcon
                                onClick={() => translationsArray.remove(index)}
                                data-test-id={`reward-category-category-name-input-remove-button-${index}`}
                              />
                            </div>
                          </Grid>
                        )}
                      </Grid>
                    );
                  },
                )}
                <OLButton
                  className={classes.addTranslation}
                  color="secondary"
                  onClick={
                    availableLanguages.filter(
                      ({ code }: { code: string }) =>
                        !values?.translations?.find(
                          (openLang: { code: string }) =>
                            openLang?.code === code,
                        ),
                    ).length
                      ? contextMenu.handleMenuOpen
                      : undefined
                  }
                  startIcon={<PlusIcon />}
                  data-test-id="reward-category-add-translation-button"
                >
                  {message({ id: "translations" }).toUpperCase()}
                </OLButton>
                <Menu
                  anchorEl={contextMenu.anchor}
                  anchorOrigin={{
                    horizontal: "right",
                    vertical: "bottom",
                  }}
                  onClose={contextMenu.handleMenuClose}
                  open={contextMenu.isMenuOpen}
                  transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                >
                  {availableLanguages
                    ?.filter(
                      ({ code }: { code: string }) =>
                        !values?.translations?.find(
                          (openLang: { code: string }) =>
                            openLang?.code === code,
                        ),
                    )
                    .map(({ code, name }: { code: string; name: string }) => (
                      <MenuItem
                        key={code}
                        onClick={() => {
                          contextMenu.handleMenuClose();
                          translationsArray.push({
                            code: code,
                            languageName: name,
                          });
                        }}
                        data-test-id={`reward-category-${name}-menu-item`}
                      >
                        {name}
                      </MenuItem>
                    ))}
                </Menu>
                <Grid item xs={12} mt={16}>
                  <Toggle
                    label={message({ id: "active" })}
                    name="active"
                    data-test-id="active"
                  />
                </Grid>
                <Grid item xs={12} mt={16}>
                  <OLButton
                    variant="contained"
                    fullWidth
                    type="submit"
                    disabled={isSubmitting}
                    data-test-id="submit-button"
                  >
                    {message({
                      id: props.editMode ? "editCategory" : "addCategory",
                    })}
                  </OLButton>
                </Grid>
              </>
            )}
          />
        </Form>
      )}
    </Formik>
  );
}
